import React, { useMemo } from 'react';
import {
  AlertTriangle, Building2, CreditCard, FileCheck, Mail, MessageSquare, Phone, Plus, StickyNote, X,
} from 'lucide-react';
import { formatoMonto } from '../compartidos/useAnalisis';
import { periodoEpisodio, SEVERIDAD } from './episodioUtils';

const notasDelCaso = (caso) => {
  const notas = (caso.timeline || []).filter((e) => e.tipo === 'NOTA' || e.tipo === 'NOTA_ALERTA').map((n) => ({
    fuente: n.tipo === 'NOTA_ALERTA' ? 'alerta' : 'caso', alerta_tipo: n.metadata?.alerta_tipo,
    tipo_nota: n.metadata?.tipo_nota || n.metadata?.accion, descripcion: n.descripcion, usuario: n.usuario, fecha: n.fecha,
  }));
  (caso.alertas || []).forEach((a) => (a.notas || []).forEach((n) => {
    if (!notas.some((x) => x.fecha === n.fecha && x.descripcion === n.comentario)) {
      notas.push({ fuente: 'alerta', alerta_tipo: a.nombre_display || a.tipo, tipo_nota: n.tipo, descripcion: n.comentario, usuario: n.usuario, fecha: n.fecha });
    }
  }));
  return notas.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).slice(0, 3);
};

const IconoNota = ({ n }) => {
  if (n.fuente === 'alerta') return <MessageSquare size={12} className="text-sky-500" />;
  if (n.tipo_nota === 'LLAMADA') return <Phone size={12} className="text-pink-500" />;
  if (n.tipo_nota === 'EMAIL') return <Mail size={12} className="text-pink-500" />;
  return <StickyNote size={12} className="text-pink-500" />;
};

const Lista = ({ titulo, icono: Icono, items, render, testid }) => (items?.length ? (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Icono size={14} /> {titulo}</h4>
    <div className="max-h-32 space-y-1 overflow-y-auto">{items.slice(0, 5).map(render)}</div>
  </div>
) : null);

// F1: episodios activos y últimas 3 notas; sin contadores ni listas vacías.
const CasoResumen = ({ caso, episodios = [], onAddNote, onVerEpisodio, onRemoveCuenta, onRemoveEmpresa }) => {
  const activos = episodios.filter((e) => e.estado === 'ACTIVO');
  const notas = useMemo(() => notasDelCaso(caso), [caso]);
  return (
    <div className="space-y-5" data-testid="caso-resumen">
      {activos.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white" data-testid="resumen-episodios-activos">
          <h3 className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-800">
            <AlertTriangle size={15} className="text-orange-500" /> Episodios activos ({activos.length})
          </h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 font-medium">Monitor</th><th className="px-4 py-2 font-medium">Período</th>
              <th className="px-4 py-2 text-right font-medium">Cantidad</th><th className="px-4 py-2 text-right font-medium">Monto</th>
            </tr></thead>
            <tbody>
              {activos.map((e) => (
                <tr key={e.id} onClick={() => onVerEpisodio(e.id)} data-testid={`resumen-episodio-${e.id}`}
                  className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-orange-50/50">
                  <td className="px-4 py-2.5"><span className={`mr-2 inline-block h-2 w-2 rounded-full ${SEVERIDAD[e.severidad] || 'bg-slate-300'}`} />{e.monitor}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{periodoEpisodio(e)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{e.cantidad}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold">{formatoMonto(e.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {notas.length === 0 ? (
        <button type="button" onClick={onAddNote} data-testid="resumen-agregar-nota"
          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-pink-300 px-3 py-1.5 text-xs font-medium text-pink-700 transition-colors hover:bg-pink-50">
          <Plus size={13} /> Agregar nota
        </button>
      ) : (
        <section className="rounded-xl border border-pink-200 bg-pink-50/40 p-4" data-testid="resumen-ultimas-notas">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-pink-800"><StickyNote size={14} /> Últimas notas</h4>
            <button type="button" onClick={onAddNote} className="rounded p-1 text-pink-600 hover:bg-pink-100" data-testid="resumen-agregar-nota"><Plus size={16} /></button>
          </div>
          <div className="space-y-2">
            {notas.map((n, i) => (
              <div key={`${n.fecha}-${i}`} className="rounded-lg bg-white p-2.5 text-xs" data-testid={`resumen-nota-${i}`}>
                <div className="mb-1 flex items-center gap-2">
                  <IconoNota n={n} />
                  <span className="font-medium text-slate-700">{n.fuente === 'alerta' ? `Episodio: ${n.alerta_tipo || '—'}` : (n.tipo_nota || 'Nota')}</span>
                  <span className="ml-auto text-[10px] text-slate-400">{n.usuario} · {new Date(n.fecha).toLocaleDateString('es-AR')}</span>
                </div>
                <p className="line-clamp-2 text-slate-600">{n.descripcion}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Lista titulo="Cuentas relacionadas" icono={CreditCard} items={caso.cuentas_relacionadas} testid="resumen-cuentas"
          render={(c) => (
            <div key={c} className="flex items-center justify-between rounded bg-slate-50 p-1.5 text-xs">
              <span className="font-mono">{c}</span>
              <button type="button" onClick={() => onRemoveCuenta(c)} className="text-red-500 hover:text-red-700"><X size={12} /></button>
            </div>
          )} />
        <Lista titulo="Empresas relacionadas" icono={Building2} items={caso.empresas_relacionadas} testid="resumen-empresas"
          render={(emp) => (
            <div key={emp.id} className="flex items-center justify-between rounded bg-slate-50 p-1.5 text-xs">
              <span className="max-w-[180px] truncate">{emp.razon_social || emp.id}</span>
              <button type="button" onClick={() => onRemoveEmpresa(emp.id)} className="text-red-500 hover:text-red-700"><X size={12} /></button>
            </div>
          )} />
        <Lista titulo="Solicitudes vinculadas" icono={FileCheck} items={caso.solicitudes_vinculadas} testid="resumen-solicitudes"
          render={(s, i) => (
            <div key={s.id || i} className="flex items-center justify-between rounded bg-slate-50 p-1.5 text-xs">
              <span className="font-medium text-slate-700">{s.referencia} · {s.tipo}</span>
              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] text-slate-600">{s.estado}</span>
            </div>
          )} />
      </div>
    </div>
  );
};

export default CasoResumen;
